import type { MindmapFarbe, MindmapKnoten } from '@beholder/shared/module/mindmap';
import { AST_FARBEN } from './symbole';

/** Baum-Sicht auf die flache Knotenliste: Kinder (sortiert), Wurzel, Ebenen, Pfade, Astfarben. */
export interface Baum {
  wurzel: MindmapKnoten | null;
  nachId: Map<string, MindmapKnoten>;
  kinder: Map<string, MindmapKnoten[]>;
  ebene: Map<string, number>;
  /** Gliederungsnummer, z. B. „2.1.3“ (Wurzel: leer). */
  nummer: Map<string, string>;
  astFarbe: Map<string, MindmapFarbe>;
}

export function sortiere(a: MindmapKnoten, b: MindmapKnoten) {
  return a.reihenfolge - b.reihenfolge || a.id.localeCompare(b.id);
}

export function baueBaum(knoten: Iterable<MindmapKnoten>): Baum {
  const nachId = new Map<string, MindmapKnoten>();
  const kinder = new Map<string, MindmapKnoten[]>();
  let wurzel: MindmapKnoten | null = null;
  for (const k of knoten) nachId.set(k.id, k);
  for (const k of nachId.values()) {
    if (k.elternId === null) {
      if (!wurzel || k.reihenfolge < wurzel.reihenfolge) wurzel = k;
      continue;
    }
    if (!nachId.has(k.elternId)) continue; // Waise (Eltern gerade gelöscht)
    if (!kinder.has(k.elternId)) kinder.set(k.elternId, []);
    kinder.get(k.elternId)!.push(k);
  }
  for (const l of kinder.values()) l.sort(sortiere);
  const ebene = new Map<string, number>();
  const nummer = new Map<string, string>();
  const astFarbe = new Map<string, MindmapFarbe>();
  if (wurzel) {
    const lauf = (k: MindmapKnoten, e: number, nr: string, farbe: MindmapFarbe | null) => {
      ebene.set(k.id, e);
      nummer.set(k.id, nr);
      if (farbe) astFarbe.set(k.id, farbe);
      (kinder.get(k.id) ?? []).forEach((c, i) => {
        const f = e === 0 ? (c.farbe ?? AST_FARBEN[i % AST_FARBEN.length]!) : (c.farbe ?? farbe);
        lauf(c, e + 1, nr ? `${nr}.${i + 1}` : String(i + 1), f);
      });
    };
    lauf(wurzel, 0, '', wurzel.farbe);
  }
  return { wurzel, nachId, kinder, ebene, nummer, astFarbe };
}

/** Alle Knoten in Lesereihenfolge (Tiefensuche). Mit `zu` werden eingeklappte Äste übersprungen. */
export function reihenfolge(b: Baum, zu?: Set<string>): MindmapKnoten[] {
  const aus: MindmapKnoten[] = [];
  if (!b.wurzel) return aus;
  const lauf = (k: MindmapKnoten) => {
    aus.push(k);
    if (zu?.has(k.id)) return;
    for (const c of b.kinder.get(k.id) ?? []) lauf(c);
  };
  lauf(b.wurzel);
  return aus;
}

export function nachkommen(b: Baum, id: string): string[] {
  const aus: string[] = [];
  const lauf = (x: string) => {
    for (const c of b.kinder.get(x) ?? []) {
      aus.push(c.id);
      lauf(c.id);
    }
  };
  lauf(id);
  return aus;
}

export function vorfahren(b: Baum, id: string): MindmapKnoten[] {
  const aus: MindmapKnoten[] = [];
  let k = b.nachId.get(id);
  let schutz = 0;
  while (k?.elternId && schutz++ < 500) {
    k = b.nachId.get(k.elternId);
    if (k) aus.unshift(k);
  }
  return aus;
}

export function pfadText(b: Baum, id: string): string {
  const k = b.nachId.get(id);
  return [...vorfahren(b, id), ...(k ? [k] : [])].map((x) => x.titel).join(' › ');
}

export function geschwister(b: Baum, id: string): MindmapKnoten[] {
  const k = b.nachId.get(id);
  if (!k?.elternId) return k ? [k] : [];
  return b.kinder.get(k.elternId) ?? [];
}

/** Reihenfolge-Wert für das Einsortieren an Stelle `index` unter `elternId` (vorläufig, bis der Server neu nummeriert). */
export function zwischenwert(b: Baum, elternId: string, index: number, ohne?: string): number {
  const l = (b.kinder.get(elternId) ?? []).filter((x) => x.id !== ohne);
  if (!l.length) return 0;
  const vor = l[index - 1];
  const nach = l[index];
  if (!vor) return nach!.reihenfolge - 1;
  if (!nach) return vor.reihenfolge + 1;
  return (vor.reihenfolge + nach.reihenfolge) / 2;
}
